// src/pages/ContentionPage.jsx
// Route: /contention/:token

import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import "./Contention.css";

const API = "https://us-central1-anonymous-complaint-portal.cloudfunctions.net/poini";

// ── tiny countdown hook ──────────────────────────────────────
function useCountdown(expiresAt) {
  const [left, setLeft] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => {
      const diff = expiresAt - Date.now();
      setLeft(Math.max(0, diff));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (left === null) return "";
  const h = Math.floor(left / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ── states ───────────────────────────────────────────────────
// "loading" | "valid" | "expired" | "used" | "invalid" | "submitted" | "submitting" | "error"

export default function ContentionPage() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [expiresAt, setExpiresAt] = useState(null);
  const [message, setMessage] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const countdown = useCountdown(expiresAt);
  const textareaRef = useRef(null);

  // ── validate on mount ──────────────────────────────────────
  useEffect(() => {
    if (!token || token.length !== 64) {
      setState("invalid");
      return;
    }

    fetch(`${API}/contention/${token}/validate`)
      .then(async (r) => {
        const data = await r.json();
        if (r.ok && data.valid) {
          setExpiresAt(data.expiresAt);
          setState("valid");
        } else if (data.error === "already_used") {
          setState("used");
        } else if (data.error === "expired") {
          setState("expired");
        } else {
          setState("invalid");
        }
      })
      .catch(() => setState("invalid"));
  }, [token]);

  // auto-expire if countdown hits zero
  useEffect(() => {
    if (expiresAt && Date.now() > expiresAt && state === "valid") {
      setState("expired");
    }
  }, [countdown, expiresAt, state]);

  // ── submit ─────────────────────────────────────────────────
  async function handleSubmit() {
    if (message.trim().length < 10) {
      setErrorMsg("Please write at least 10 characters.");
      return;
    }
    setErrorMsg("");
    setState("submitting");

    try {
      const r = await fetch(`${API}/contention/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: message.trim() }),
      });

      const data = await r.json();

      if (r.ok && data.success) {
        setState("submitted");
      } else if (data.error === "already_used") {
        setState("used");
      } else if (data.error === "expired") {
        setState("expired");
      } else {
        setErrorMsg(data.error || "Submission failed. Please try again.");
        setState("valid");
      }
    } catch {
      setErrorMsg("Network error. Please check your connection and try again.");
      setState("valid");
    }
  }

  // ── render helpers ─────────────────────────────────────────
  const StatusScreen = ({ icon, title, subtitle, className = "" }) => (
    <div className={`cp-status ${className}`}>
      <div className="cp-status-icon">{icon}</div>
      <h2 className="cp-status-title">{title}</h2>
      <p className="cp-status-sub">{subtitle}</p>
    </div>
  );

  // ── render ─────────────────────────────────────────────────
  return (
    <div className="cp-root">
      {/* background grid */}
      <div className="cp-bg" aria-hidden="true">
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={i} className="cp-bg-line" style={{ "--i": i }} />
        ))}
      </div>

      <div className="cp-card">
        {/* header */}
        <header className="cp-header">
          {/* <div className="cp-seal">
            <span>EC</span>
          </div> */}
          <div className="cp-header-text">
            <span className="cp-header-top">Election Commission</span>
            <span className="cp-header-bot">Indian Institute of Technology Kanpur</span>
          </div>
        </header>

        <div className="cp-divider" />

        {/* ── LOADING ── */}
        {state === "loading" && (
          <div className="cp-loading">
            <div className="cp-spinner" />
            <p>Verifying your secure link…</p>
          </div>
        )}

        {/* ── INVALID ── */}
        {state === "invalid" && (
          <StatusScreen
            icon="⚠"
            title="Invalid Link"
            subtitle="This contention link is invalid or malformed. Please check the link in your email."
            className="cp-status--warn"
          />
        )}

        {/* ── EXPIRED ── */}
        {state === "expired" && (
          <StatusScreen
            icon="⏱"
            title="Link Expired"
            subtitle="This contention link has expired (24-hour limit). Please contact the Election Commission directly if you still wish to contest."
            className="cp-status--warn"
          />
        )}

        {/* ── ALREADY USED ── */}
        {state === "used" && (
          <StatusScreen
            icon="✓"
            title="Already Submitted"
            subtitle="Your contention has already been recorded using this link. Each link is single-use only. You will be contacted within 2 days."
            className="cp-status--ok"
          />
        )}

        {/* ── SUCCESS ── */}
        {state === "submitted" && (
          <div className="cp-submitted">
            <div className="cp-check">
              <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="26" cy="26" r="25" stroke="currentColor" strokeWidth="2" />
                <path d="M14 26l8 8 16-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2>Contention Filed</h2>
            <p>Your contention has been forwarded to the Election Commission. A confirmation has been sent to your registered email.</p>
            <p className="cp-timeline">Resolution expected within <strong>2 days</strong>.</p>
          </div>
        )}

        {/* ── VALID FORM ── */}
        {(state === "valid" || state === "submitting") && (
          <div className="cp-form">
            <div className="cp-form-header">
              <h2 className="cp-form-title">File Contention</h2>
              {countdown && (
                <div className="cp-timer" title="Time remaining">
                  <span className="cp-timer-label">Expires in</span>
                  <span className="cp-timer-count">{countdown}</span>
                </div>
              )}
            </div>

            <p className="cp-form-notice">
              This is a <strong>one-time secure form</strong>. Once submitted, this link will be permanently invalidated. Describe your contention clearly and thoroughly.
            </p>

            <div className="cp-field">
              <label className="cp-label" htmlFor="contention-msg">
                Your Contention
              </label>
              <textarea
                id="contention-msg"
                ref={textareaRef}
                className="cp-textarea"
                placeholder="Clearly state the grounds on which you contest this penalty. Include any relevant facts, references, or supporting context…"
                value={message}
                maxLength={5000}
                disabled={state === "submitting"}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setCharCount(e.target.value.length);
                  if (errorMsg) setErrorMsg("");
                }}
              />
              <div className="cp-field-footer">
                {errorMsg && <span className="cp-error">{errorMsg}</span>}
                <span className="cp-char-count">{charCount} / 5000</span>
              </div>
            </div>

            {/* <div className="cp-security-note">
              <span className="cp-lock">🔒</span>
              This form does not display or transmit your personal details. Your identity is protected.
            </div> */}

            <button
              className="cp-btn"
              disabled={state === "submitting" || message.trim().length < 10}
              onClick={handleSubmit}
            >
              {state === "submitting" ? (
                <>
                  <span className="cp-btn-spinner" />
                  Submitting…
                </>
              ) : (
                "Submit Contention"
              )}
            </button>
          </div>
        )}

        {/* footer */}
        <footer className="cp-footer">
          ΠΟΙΝΗ Penalty Portal &nbsp;·&nbsp; Election Commission, IIT Kanpur
        </footer>
      </div>
    </div>
  );
}